/**
 * The thirty-six archetypes: the twelve of 1987 (03 §2) in the manual's order, then the expansion's
 * twenty-four (10 §2). The expansion entries (13-36) are real generators: `docs/spec/10-expansion-missions.md`.
 */

import { type ArchetypeId, ORIGINAL_ARCHETYPES } from '../../sim/types';
import { ammoBarges } from './ammoBarges';
import { artilleryBattery } from './artilleryBattery';
import { beacon } from './beacon';
import { blackBox } from './blackBox';
import { blowBridge } from './blowBridge';
import { captureOfficer } from './captureOfficer';
import { coastalGuns } from './coastalGuns';
import { codeBook } from './codeBook';
import { courierAmbush } from './courierAmbush';
import { defector } from './defector';
import { delayedSabotage } from './delayedSabotage';
import { diversion } from './diversion';
import { downedPilot } from './downedPilot';
import { eliminateGeneral } from './eliminateGeneral';
import { enemyAircraft } from './enemyAircraft';
import { forwardObserver } from './forwardObserver';
import { fuelDepot } from './fuelDepot';
import { holdCrossing } from './holdCrossing';
import { hostages } from './hostages';
import { mineRoad } from './mineRoad';
import { missileConvoy } from './missileConvoy';
import { munitionsDepot } from './munitionsDepot';
import { partisanRendezvous } from './partisanRendezvous';
import { photographAircraft } from './photographAircraft';
import { photographConvoy } from './photographConvoy';
import { pipeline } from './pipeline';
import { powCamp } from './powCamp';
import { powerStation } from './powerStation';
import { prisonBreak } from './prisonBreak';
import { radarArray } from './radarArray';
import { relayMast } from './relayMast';
import { samSite } from './samSite';
import { snatchScientist } from './snatchScientist';
import { stealPlans } from './stealPlans';
import { supplyTrain } from './supplyTrain';
import { wiretap } from './wiretap';
import type { ArchetypeSpec } from './types';

export type { ArchetypeSpec } from './types';

export const ARCHETYPES: Record<ArchetypeId, ArchetypeSpec> = {
  'munitions-depot': munitionsDepot,
  'code-book': codeBook,
  'enemy-aircraft': enemyAircraft,
  'capture-officer': captureOfficer,
  pipeline,
  'radar-array': radarArray,
  'sam-site': samSite,
  'pow-camp': powCamp,
  'photograph-aircraft': photographAircraft,
  hostages,
  diversion,
  'delayed-sabotage': delayedSabotage,
  // The expansion (10 §2), missions 13-36: every one a real generator with its own asserts, as the twelve above.
  'blow-bridge': blowBridge,
  'supply-train': supplyTrain,
  'artillery-battery': artilleryBattery,
  'relay-mast': relayMast,
  'missile-convoy': missileConvoy,
  'fuel-depot': fuelDepot,
  'coastal-guns': coastalGuns,
  'ammo-barges': ammoBarges,
  'steal-plans': stealPlans,
  wiretap,
  'photograph-convoy': photographConvoy,
  'black-box': blackBox,
  'forward-observer': forwardObserver,
  'courier-ambush': courierAmbush,
  'downed-pilot': downedPilot,
  defector,
  'eliminate-general': eliminateGeneral,
  'snatch-scientist': snatchScientist,
  'partisan-rendezvous': partisanRendezvous,
  'prison-break': prisonBreak,
  'power-station': powerStation,
  'mine-road': mineRoad,
  beacon,
  'hold-crossing': holdCrossing,
};

/** All thirty-six, in mission-number order. */
export const ARCHETYPE_IDS = Object.keys(ARCHETYPES) as ArchetypeId[];

/** The 1987 twelve: what Classic mode lists. */
export const ORIGINAL_IDS: readonly ArchetypeId[] = ORIGINAL_ARCHETYPES;
